import {
  Clock,
  UserCircle,
  Globe,
  Phone,
  History,
  Building,
  StickyNote,
  Megaphone,
  MapPin,
  Ticket,
} from 'lucide-react';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Label } from '@easyflow/ui-frontend/components/ui/label';

export interface PreCallHooksConfig {
  inject_datetime?: boolean;
  auto_crm_lookup?: boolean;
  inject_preferred_language?: boolean;
  inject_last_ticket?: boolean;
  inject_caller_number?: boolean;
  inject_interaction_history?: boolean;
  inject_business_hours?: boolean;
  inject_agent_notes?: boolean;
  inject_campaign_context?: boolean;
  inject_geo_context?: boolean;
}

export type AgentHookType = 'voice' | 'chatbot' | 'assistant';

interface HookDefinition {
  key: keyof PreCallHooksConfig;
  name: string;
  description: string;
  icon: React.ReactNode;
  applicableTo: AgentHookType[];
}

const HOOK_DEFINITIONS: HookDefinition[] = [
  { key: 'inject_datetime', name: 'Data e Ora', description: 'Inietta giorno, data e ora corrente', icon: <Clock className="w-4 h-4" />, applicableTo: ['voice', 'chatbot', 'assistant'] },
  { key: 'auto_crm_lookup', name: 'Lookup CRM Automatico', description: 'Cerca chiamante/utente nel CRM per nome, azienda, email', icon: <UserCircle className="w-4 h-4" />, applicableTo: ['voice', 'chatbot', 'assistant'] },
  { key: 'inject_preferred_language', name: 'Lingua Preferita', description: 'Rileva lingua dalle interazioni precedenti', icon: <Globe className="w-4 h-4" />, applicableTo: ['voice', 'chatbot', 'assistant'] },
  { key: 'inject_last_ticket', name: 'Ultimo Ticket/Ordine', description: 'Cerca ultimo ticket o ordine aperto del contatto', icon: <Ticket className="w-4 h-4" />, applicableTo: ['voice', 'chatbot', 'assistant'] },
  { key: 'inject_caller_number', name: 'Numero Chiamante', description: 'Inietta il numero del chiamante nelle istruzioni', icon: <Phone className="w-4 h-4" />, applicableTo: ['voice'] },
  { key: 'inject_interaction_history', name: 'Storico Interazioni', description: 'Riepilogo ultime 5 interazioni (chiamate, messaggi)', icon: <History className="w-4 h-4" />, applicableTo: ['voice', 'assistant'] },
  { key: 'inject_business_hours', name: 'Orari Aziendali', description: 'Stato aperto/chiuso e orari del giorno corrente', icon: <Building className="w-4 h-4" />, applicableTo: ['voice', 'assistant'] },
  { key: 'inject_agent_notes', name: 'Note Agente', description: "Memorie dell'agente su questo contatto specifico", icon: <StickyNote className="w-4 h-4" />, applicableTo: ['voice', 'assistant'] },
  { key: 'inject_campaign_context', name: 'Contesto Campagna', description: 'Per outbound: info campagna attiva e offerta', icon: <Megaphone className="w-4 h-4" />, applicableTo: ['voice'] },
  { key: 'inject_geo_context', name: 'Contesto Geografico', description: 'Area geografica dal prefisso telefonico', icon: <MapPin className="w-4 h-4" />, applicableTo: ['voice'] },
];

interface PreCallHooksSectionProps {
  hooks: PreCallHooksConfig;
  onChange: (hooks: PreCallHooksConfig) => void;
  agentType: AgentHookType;
}

export function normalizePreCallHooks(input: unknown): PreCallHooksConfig {
  if (!input) return {};
  if (Array.isArray(input)) {
    const out: PreCallHooksConfig = {};
    for (const item of input) {
      if (typeof item === 'string' && item) {
        out[item as keyof PreCallHooksConfig] = true;
      }
    }
    return out;
  }
  if (typeof input === 'object') return input as PreCallHooksConfig;
  return {};
}

export default function PreCallHooksSection({ hooks, onChange, agentType }: PreCallHooksSectionProps) {
  const applicableHooks = HOOK_DEFINITIONS.filter(h => h.applicableTo.includes(agentType));

  const handleToggle = (key: keyof PreCallHooksConfig) => {
    onChange({ ...hooks, [key]: !hooks[key] });
  };

  const enabledCount = applicableHooks.filter(h => hooks[h.key]).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
          Hook Pre-Interazione
        </Label>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {enabledCount}/{applicableHooks.length} attivi
        </span>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Contesto dinamico iniettato automaticamente prima di ogni interazione.
      </p>
      <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
        {applicableHooks.map((hook) => (
          <div
            key={hook.key}
            className="flex items-start gap-3 p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 dark:bg-gray-800/50 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700/50 transition-colors"
          >
            <div className="mt-0.5 text-gray-500 dark:text-gray-400">{hook.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-200">{hook.name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{hook.description}</div>
            </div>
            <Switch
              checked={!!hooks[hook.key]}
              onCheckedChange={() => handleToggle(hook.key)}
              className="shrink-0"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
